import React from "react";
import BookingDetailsCard from "./BookingDetailsCard";
import { formatTime } from "../../../utils/countryLocaleMap";
import { useWidgetLocale } from "../hooks/useWidgetLocale";

/**
 * A booking created alongside the primary one, normalised for display. The
 * `/submitBooking` and `/checkDeposit` payloads name these fields differently,
 * so each flow maps its own response onto this single display shape.
 */
export interface LinkedBookingSummary {
  /** Stable identifier used as the list key; falls back to the reference or index. */
  id?: string;
  /** Booking reference of this linked booking (e.g. "DMN-415883906030"). */
  reference?: string;
  /** Booking type name (e.g. "Lunch"). */
  typeName?: string | null;
  /** ISO date of this linked booking. */
  date?: string;
  /** Time of this linked booking, in "HH:mm" form. */
  time?: string;
  /** Covers on this linked booking. Omitted when the type captures no covers. */
  partySize?: number;
}

interface LinkedBookingsDetailsProps {
  readonly bookings: readonly LinkedBookingSummary[];
  /** Venue name, shown as the location on each card. */
  readonly location: string;
  /**
   * The booking day, already formatted for display. Every booking in a linked
   * booking falls on the same day, so the confirmation's date is reused rather
   * than formatted again per card.
   */
  readonly date: string;
  readonly currency?: string;
  readonly countryCode?: string | null;
  /** Guest name, the same for every booking in a linked booking. */
  readonly customerName?: string;
}

/**
 * LinkedBookingsDetails — renders the bookings created alongside the primary one.
 *
 * A linked booking is several bookings in their own right, each with its own
 * reference, time and booking type, so each is given the same
 * {@link BookingDetailsCard} treatment as the primary booking rather than a
 * condensed summary.
 *
 * Renders nothing when there are no linked bookings.
 */
const LinkedBookingsDetails: React.FC<LinkedBookingsDetailsProps> = ({
  bookings,
  location,
  date,
  currency,
  countryCode,
  customerName,
}) => {
  const timeLocale = useWidgetLocale(countryCode);
  if (bookings.length === 0) {
    return null;
  }

  const resolvedCountryCode = countryCode ?? "GB";

  return (
    <>
      {bookings.map((booking, index) => (
        <BookingDetailsCard
          key={booking.id ?? booking.reference ?? `linked-${index}`}
          // BookingDetailsCard omits the covers row for a party size of 0, which
          // is the right outcome for a booking type that captures no covers.
          partySize={booking.partySize ?? 0}
          date={date}
          time={
            booking.date && booking.time ? formatTime(booking.time, booking.date, resolvedCountryCode, timeLocale) : ""
          }
          location={location}
          currency={currency}
          countryCode={resolvedCountryCode}
          customerName={customerName}
          bookingType={booking.typeName ?? undefined}
          bookingRef={booking.reference}
        />
      ))}
    </>
  );
};

export default LinkedBookingsDetails;
